import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Mail, Save, Loader2, Percent, Plus, Pencil, Trash2, Timer, Tag, GripVertical } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminSettings = () => {
    const { token } = useAuth();
    const [settings, setSettings] = useState({ sales_email: '', quote_expiry_days: 30 });
    const [vatRates, setVatRates] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [vatSaving, setVatSaving] = useState(false);
    const [catSaving, setCatSaving] = useState(false);
    const [vatDialogOpen, setVatDialogOpen] = useState(false);
    const [catDialogOpen, setCatDialogOpen] = useState(false);
    const [editingRate, setEditingRate] = useState(null);
    const [editingCat, setEditingCat] = useState(null);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [deleteCatConfirmOpen, setDeleteCatConfirmOpen] = useState(false);
    const [rateToDelete, setRateToDelete] = useState(null);
    const [catToDelete, setCatToDelete] = useState(null);
    const [vatForm, setVatForm] = useState({ name: '', rate: '', active: true });
    const [catForm, setCatForm] = useState({ name: '', description: '', sort_order: 0, active: true });

    useEffect(() => { fetchAll(); }, []);

    const fetchAll = async () => {
        try {
            const [settingsRes, vatRes, catRes] = await Promise.all([
                axios.get(`${API}/admin/settings`, { headers: { Authorization: `Bearer ${token}` } }),
                axios.get(`${API}/admin/vat-rates`, { headers: { Authorization: `Bearer ${token}` } }),
                axios.get(`${API}/admin/spares-categories`, { headers: { Authorization: `Bearer ${token}` } })
            ]);
            setSettings(prev => ({ ...prev, ...settingsRes.data }));
            setVatRates(vatRes.data);
            setCategories(catRes.data);
        } catch { toast.error('Failed to load settings'); }
        finally { setLoading(false); }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await axios.put(`${API}/admin/settings`, {
                sales_email: settings.sales_email,
                quote_expiry_days: parseInt(settings.quote_expiry_days) || 30
            }, { headers: { Authorization: `Bearer ${token}` } });
            toast.success('Settings saved successfully');
        } catch { toast.error('Failed to save settings'); }
        finally { setSaving(false); }
    };

    // VAT Rate handlers
    const handleOpenVatDialog = (rate = null) => {
        if (rate) {
            setEditingRate(rate);
            setVatForm({ name: rate.name, rate: rate.rate.toString(), active: rate.active });
        } else {
            setEditingRate(null);
            setVatForm({ name: '', rate: '', active: true });
        }
        setVatDialogOpen(true);
    };

    const handleSaveVat = async () => {
        if (!vatForm.name || vatForm.rate === '') { toast.error('Please fill in all fields'); return; }
        setVatSaving(true);
        try {
            const payload = { name: vatForm.name, rate: parseFloat(vatForm.rate), active: vatForm.active };
            if (editingRate) {
                await axios.put(`${API}/admin/vat-rates/${editingRate.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
                toast.success('VAT rate updated');
            } else {
                await axios.post(`${API}/admin/vat-rates`, payload, { headers: { Authorization: `Bearer ${token}` } });
                toast.success('VAT rate created');
            }
            setVatDialogOpen(false);
            fetchAll();
        } catch { toast.error('Failed to save VAT rate'); }
        finally { setVatSaving(false); }
    };

    const handleDeleteVat = async () => {
        if (!rateToDelete) return;
        setVatSaving(true);
        try {
            await axios.delete(`${API}/admin/vat-rates/${rateToDelete.id}`, { headers: { Authorization: `Bearer ${token}` } });
            toast.success('VAT rate deleted');
            setDeleteConfirmOpen(false);
            setRateToDelete(null);
            fetchAll();
        } catch { toast.error('Failed to delete VAT rate'); }
        finally { setVatSaving(false); }
    };

    // Category handlers
    const handleOpenCatDialog = (cat = null) => {
        if (cat) {
            setEditingCat(cat);
            setCatForm({ name: cat.name, description: cat.description || '', sort_order: cat.sort_order || 0, active: cat.active });
        } else {
            setEditingCat(null);
            setCatForm({ name: '', description: '', sort_order: categories.length, active: true });
        }
        setCatDialogOpen(true);
    };

    const handleSaveCat = async () => {
        if (!catForm.name) { toast.error('Please enter a category name'); return; }
        setCatSaving(true);
        try {
            const payload = { name: catForm.name, description: catForm.description, sort_order: parseInt(catForm.sort_order) || 0, active: catForm.active };
            if (editingCat) {
                await axios.put(`${API}/admin/spares-categories/${editingCat.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
                toast.success('Category updated');
            } else {
                await axios.post(`${API}/admin/spares-categories`, payload, { headers: { Authorization: `Bearer ${token}` } });
                toast.success('Category created');
            }
            setCatDialogOpen(false);
            fetchAll();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to save category');
        } finally { setCatSaving(false); }
    };

    const handleDeleteCat = async () => {
        if (!catToDelete) return;
        setCatSaving(true);
        try {
            await axios.delete(`${API}/admin/spares-categories/${catToDelete.id}`, { headers: { Authorization: `Bearer ${token}` } });
            toast.success('Category deleted');
            setDeleteCatConfirmOpen(false);
            setCatToDelete(null);
            fetchAll();
        } catch { toast.error('Failed to delete category'); }
        finally { setCatSaving(false); }
    };

    if (loading) {
        return <div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="h-8 w-8 animate-spin text-brand-lime" /></div>;
    }

    return (
        <div className="max-w-2xl mx-auto animate-fade-in space-y-6">
            <div>
                <h1 className="text-3xl font-bold font-heading text-brand-charcoal">Settings</h1>
                <p className="text-brand-gray-500 mt-1">Configure application settings</p>
            </div>

            <form onSubmit={handleSave} className="space-y-6">
                {/* Email Notifications */}
                <Card className="border-0 shadow-lg rounded-lg">
                    <CardHeader>
                        <CardTitle className="text-xl font-bold font-heading flex items-center gap-2">
                            <Mail className="h-5 w-5 text-brand-lime" /> Email Notifications
                        </CardTitle>
                        <CardDescription>Configure where internal quote notifications are sent</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-2">
                            <Label htmlFor="salesEmail" className="text-xs font-bold uppercase tracking-wide">Sales Team Email</Label>
                            <p className="text-sm text-brand-gray-500 mb-2">All quote notifications will be sent to this email address</p>
                            <div className="relative">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-brand-gray-400" />
                                <Input id="salesEmail" type="email" value={settings.sales_email || ''} onChange={(e) => setSettings(prev => ({ ...prev, sales_email: e.target.value }))} placeholder="sales@company.com" className="h-12 pl-10" required data-testid="sales-email-input" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Quote Expiry */}
                <Card className="border-0 shadow-lg rounded-lg">
                    <CardHeader>
                        <CardTitle className="text-xl font-bold font-heading flex items-center gap-2">
                            <Timer className="h-5 w-5 text-brand-lime" /> Quote Expiry
                        </CardTitle>
                        <CardDescription>Automatically expire quotes that haven't been followed up</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-2">
                            <Label htmlFor="expiryDays" className="text-xs font-bold uppercase tracking-wide">Expiry Period (Days)</Label>
                            <p className="text-sm text-brand-gray-500 mb-2">Quotes marked as "Sent" with no follow-up will be automatically expired after this many days. Runs daily at 3AM.</p>
                            <Input id="expiryDays" type="number" min="1" value={settings.quote_expiry_days || 30} onChange={(e) => setSettings(prev => ({ ...prev, quote_expiry_days: e.target.value }))} className="h-12 max-w-[200px]" data-testid="quote-expiry-days-input" />
                        </div>
                    </CardContent>
                </Card>

                <div className="flex justify-end">
                    <Button type="submit" className="h-12 bg-[#7AC943] hover:bg-[#68B035] text-black font-bold uppercase tracking-wide px-8" disabled={saving} data-testid="save-settings-btn">
                        {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Save className="h-5 w-5 mr-2" /> Save Settings</>}
                    </Button>
                </div>
            </form>

            {/* VAT Rates */}
            <Card className="border-0 shadow-lg rounded-lg">
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <div>
                            <CardTitle className="text-xl font-bold font-heading flex items-center gap-2">
                                <Percent className="h-5 w-5 text-brand-lime" /> VAT Rates
                            </CardTitle>
                            <CardDescription>Manage VAT rates applied to spares packs</CardDescription>
                        </div>
                        <Button onClick={() => handleOpenVatDialog()} className="bg-[#7AC943] hover:bg-[#68B035] text-black" data-testid="add-vat-rate-btn">
                            <Plus className="h-4 w-4 mr-2" /> Add Rate
                        </Button>
                    </div>
                </CardHeader>
                <CardContent>
                    {vatRates.length === 0 ? (
                        <div className="text-center py-8">
                            <Percent className="h-10 w-10 mx-auto text-brand-gray-300 mb-2" />
                            <p className="text-brand-gray-500">No VAT rates configured</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {vatRates.map(rate => (
                                <div key={rate.id} className="flex items-center justify-between p-4 bg-brand-gray-50 rounded-lg" data-testid={`vat-rate-${rate.id}`}>
                                    <div className="flex items-center gap-4">
                                        <div className="h-12 w-12 bg-brand-lime/20 flex items-center justify-center">
                                            <span className="text-lg font-bold text-brand-charcoal">{rate.rate}%</span>
                                        </div>
                                        <div>
                                            <p className="font-bold text-brand-charcoal">{rate.name}</p>
                                            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium mt-1 ${rate.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                                                {rate.active ? 'Active' : 'Inactive'}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="flex gap-1">
                                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleOpenVatDialog(rate)} data-testid={`edit-vat-rate-${rate.id}`}>
                                            <Pencil className="h-4 w-4" />
                                        </Button>
                                        <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => { setRateToDelete(rate); setDeleteConfirmOpen(true); }} data-testid={`delete-vat-rate-${rate.id}`}>
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* VAT Rate Dialog */}
            <Dialog open={vatDialogOpen} onOpenChange={setVatDialogOpen}>
                <DialogContent className="sm:max-w-[400px]">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl">{editingRate ? 'Edit VAT Rate' : 'Add VAT Rate'}</DialogTitle>
                        <DialogDescription>{editingRate ? 'Update the VAT rate details' : 'Create a new VAT rate'}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label className="text-xs font-bold uppercase tracking-wide">Name</Label>
                            <Input value={vatForm.name} onChange={(e) => setVatForm({ ...vatForm, name: e.target.value })} placeholder="e.g., Standard, Reduced, Zero" className="h-11" data-testid="vat-name-input" />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-xs font-bold uppercase tracking-wide">Rate (%)</Label>
                            <Input type="number" step="0.1" min="0" value={vatForm.rate} onChange={(e) => setVatForm({ ...vatForm, rate: e.target.value })} placeholder="20.0" className="h-11" data-testid="vat-rate-input" />
                        </div>
                        <div className="flex items-center justify-between">
                            <Label className="text-sm font-medium">Active</Label>
                            <Switch checked={vatForm.active} onCheckedChange={(checked) => setVatForm({ ...vatForm, active: checked })} data-testid="vat-active-switch" />
                        </div>
                    </div>
                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setVatDialogOpen(false)}>Cancel</Button>
                        <Button onClick={handleSaveVat} className="bg-[#7AC943] hover:bg-[#68B035] text-black" disabled={vatSaving} data-testid="save-vat-rate-btn">
                            {vatSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Spares Categories */}
            <Card className="border-0 shadow-lg rounded-lg">
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <div>
                            <CardTitle className="text-xl font-bold font-heading flex items-center gap-2">
                                <Tag className="h-5 w-5 text-brand-lime" /> Spares Categories
                            </CardTitle>
                            <CardDescription>Organise spares packs into categories</CardDescription>
                        </div>
                        <Button onClick={() => handleOpenCatDialog()} className="bg-[#7AC943] hover:bg-[#68B035] text-black" data-testid="add-category-btn">
                            <Plus className="h-4 w-4 mr-2" /> Add Category
                        </Button>
                    </div>
                </CardHeader>
                <CardContent>
                    {categories.length === 0 ? (
                        <div className="text-center py-8">
                            <Tag className="h-10 w-10 mx-auto text-brand-gray-300 mb-2" />
                            <p className="text-brand-gray-500">No categories configured</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {categories.map(cat => (
                                <div key={cat.id} className="flex items-center justify-between p-4 bg-brand-gray-50 rounded-lg" data-testid={`category-${cat.id}`}>
                                    <div className="flex items-center gap-4">
                                        <div className="h-12 w-12 bg-brand-lime/20 flex items-center justify-center rounded-lg">
                                            <Tag className="h-5 w-5 text-brand-charcoal" />
                                        </div>
                                        <div>
                                            <p className="font-bold text-brand-charcoal">{cat.name}</p>
                                            {cat.description && <p className="text-xs text-brand-gray-500 mt-0.5">{cat.description}</p>}
                                            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium mt-1 ${cat.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                                                {cat.active ? 'Active' : 'Inactive'}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="flex gap-1">
                                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleOpenCatDialog(cat)} data-testid={`edit-category-${cat.id}`}>
                                            <Pencil className="h-4 w-4" />
                                        </Button>
                                        <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => { setCatToDelete(cat); setDeleteCatConfirmOpen(true); }} data-testid={`delete-category-${cat.id}`}>
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Category Dialog */}
            <Dialog open={catDialogOpen} onOpenChange={setCatDialogOpen}>
                <DialogContent className="sm:max-w-[400px]">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl">{editingCat ? 'Edit Category' : 'Add Category'}</DialogTitle>
                        <DialogDescription>{editingCat ? 'Update the category details' : 'Create a new spares category'}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label className="text-xs font-bold uppercase tracking-wide">Name</Label>
                            <Input value={catForm.name} onChange={(e) => setCatForm({ ...catForm, name: e.target.value })} placeholder="e.g., Spares Packs, Individual Spares" className="h-11" data-testid="category-name-input" />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-xs font-bold uppercase tracking-wide">Description</Label>
                            <Input value={catForm.description} onChange={(e) => setCatForm({ ...catForm, description: e.target.value })} placeholder="Optional description" className="h-11" data-testid="category-description-input" />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-xs font-bold uppercase tracking-wide">Sort Order</Label>
                            <Input type="number" min="0" value={catForm.sort_order} onChange={(e) => setCatForm({ ...catForm, sort_order: e.target.value })} className="h-11 max-w-[120px]" data-testid="category-sort-order-input" />
                        </div>
                        <div className="flex items-center justify-between">
                            <Label className="text-sm font-medium">Active</Label>
                            <Switch checked={catForm.active} onCheckedChange={(checked) => setCatForm({ ...catForm, active: checked })} data-testid="category-active-switch" />
                        </div>
                    </div>
                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setCatDialogOpen(false)}>Cancel</Button>
                        <Button onClick={handleSaveCat} className="bg-[#7AC943] hover:bg-[#68B035] text-black" disabled={catSaving} data-testid="save-category-btn">
                            {catSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Category Confirm */}
            <Dialog open={deleteCatConfirmOpen} onOpenChange={setDeleteCatConfirmOpen}>
                <DialogContent className="sm:max-w-[400px]">
                    <DialogHeader>
                        <DialogTitle className="text-red-600">Delete Category</DialogTitle>
                        <DialogDescription>Are you sure you want to delete "{catToDelete?.name}"? Packs in this category will become uncategorised.</DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setDeleteCatConfirmOpen(false)}>Cancel</Button>
                        <Button variant="destructive" onClick={handleDeleteCat} disabled={catSaving} data-testid="confirm-delete-category-btn">
                            {catSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Delete'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete VAT Confirm */}
            <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
                <DialogContent className="sm:max-w-[400px]">
                    <DialogHeader>
                        <DialogTitle className="text-red-600">Delete VAT Rate</DialogTitle>
                        <DialogDescription>Are you sure you want to delete "{rateToDelete?.name} ({rateToDelete?.rate}%)"? Packs using this rate will need to be reassigned.</DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
                        <Button variant="destructive" onClick={handleDeleteVat} disabled={vatSaving} data-testid="confirm-delete-vat-btn">
                            {vatSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Delete'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AdminSettings;
